// src/components/Career.js
import React, { useState } from 'react';
import './Career.css';

const Career = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    resume: null,
    coverLetter: '',
    subscribeEmail: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [subscribeStatus, setSubscribeStatus] = useState({ type: '', message: '' });

  const jobOpenings = [
    {
      id: 1,
      title: 'Medical Coder',
      type: 'Full-time',
      location: 'Remote / USA',
      skills: [
        'Develop and maintain detailed medical records using electronic health record (EHR) systems such as Epic or Cerner.',
        'Perform coding audits to ensure accuracy and compliance with coding guidelines.',
        'Collaborate with clinical staff to understand patient care needs and provide accurate documentation.'
      ],
      requirements: [
        'Certified Professional Coder (CPC) or equivalent certification',
        'Minimum 2 years of medical coding experience',
        'Proficient in ICD-10, CPT, and HCPCS coding systems',
        'Strong attention to detail and analytical skills'
      ]
    },
    {
      id: 2,
      title: 'Professional Sales Representative',
      type: 'Full-time',
      location: 'Multiple Locations',
      skills: [
        'Develop and maintain sales relationships with physicians, hospitals, and other healthcare providers.',
        'Utilize CRM systems to track sales opportunities and manage pipeline.',
        'Provide training and support to sales representatives.'
      ],
      requirements: [
        'Bachelor\'s degree in Business, Marketing, or related field',
        '3+ years of healthcare sales experience',
        'Proven track record of meeting sales targets',
        'Excellent communication and negotiation skills'
      ]
    },
    {
      id: 3,
      title: 'Medical Biller',
      type: 'Full-time',
      location: 'Remote / USA',
      skills: [
        'Prepare and submit claims for reimbursement.',
        'Process insurance payments and resolve billing discrepancies.',
        'Maintain up-to-date knowledge of healthcare regulations and billing codes.'
      ],
      requirements: [
        'Certified Medical Biller (CMB) or equivalent',
        '2+ years of medical billing experience',
        'Knowledge of ICD-10, CPT, and HCPCS codes',
        'Experience with major EHR systems'
      ]
    },
    {
      id: 4,
      title: 'AR Follow-Up Specialist',
      type: 'Full-time',
      location: 'Remote',
      skills: [
        'Follow up on unpaid claims and denials with insurance companies',
        'Identify and resolve accounts receivable issues',
        'Document all collection activities accurately'
      ],
      requirements: [
        '2+ years of AR follow-up experience',
        'Knowledge of insurance billing and collections',
        'Strong problem-solving and communication skills',
        'Experience with aging reports'
      ]
    },
    {
      id: 5,
      title: 'Credentialing Specialist',
      type: 'Full-time',
      location: 'Remote',
      skills: [
        'Manage provider enrollment and credentialing process',
        'Maintain accurate provider data in CAQH and other systems',
        'Coordinate with insurance companies for network participation'
      ],
      requirements: [
        '2+ years of credentialing experience',
        'Knowledge of CAQH, NPPES, and PECOS systems',
        'Understanding of payer enrollment requirements',
        'Detail-oriented with excellent organizational skills'
      ]
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, resume: e.target.files[0] }));
  };

  const handleSubscribeChange = (e) => {
    setSubscribeStatus({ type: '', message: '' });
    setFormData(prev => ({ ...prev, subscribeEmail: e.target.value }));
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!formData.subscribeEmail) {
      setSubscribeStatus({ type: 'error', message: 'Please enter your email address.' });
      return;
    }

    try {
      const response = await fetch('https://formspree.io/f/xrejwqgl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email: formData.subscribeEmail, type: 'newsletter' })
      });

      if (response.ok) {
        setSubscribeStatus({ type: 'success', message: 'Thank you for subscribing! You will receive updates soon.' });
        setFormData(prev => ({ ...prev, subscribeEmail: '' }));
      } else {
        throw new Error('Subscription failed');
      }
    } catch (error) {
      setSubscribeStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    }
    setTimeout(() => setSubscribeStatus({ type: '', message: '' }), 5000);
  };

  const handleJobApplication = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formspree.io/f/xrejwqgl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          position: formData.position,
          experience: formData.experience,
          coverLetter: formData.coverLetter,
          type: 'job_application'
        })
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Your application has been submitted successfully! We will contact you soon.' });
        setFormData(prev => ({
          ...prev,
          fullName: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          coverLetter: '',
          resume: null
        }));
        setSelectedJob(null);
      } else {
        throw new Error('Application failed');
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus({ type: '', message: '' }), 5000);
    }
  };

  const openJobModal = (job) => {
    setSelectedJob(job);
    setFormData(prev => ({ ...prev, position: job.title }));
  };

  const closeJobModal = () => {
    setSelectedJob(null);
    setSubmitStatus({ type: '', message: '' });
  };

  return (
    <main className="career-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="container">
          <span className="breadcrumb-item">Home</span>
          <span className="breadcrumb-separator">→</span>
          <span className="breadcrumb-item active">Careers</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="career-hero">
        <div className="container">
          <div className="career-hero-content">
            <h1 className="career-hero-title">Careers at Providers Care Billing LLC</h1>
            <p className="career-hero-description">
              At Providers Care Billing LLC we believe our greatest asset is our people. We are a forward-thinking medical 
              billing company dedicated to innovation, excellence, and the professional growth of our employees. We value 
              diversity and foster an inclusive environment where every voice is heard and respected. Join us to be part of 
              a team that inspires, supports, and empowers you to succeed.
            </p>
          </div>
        </div>
      </section>

      {/* Open Roles Section */}
      <section className="open-roles">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">OPEN ROLES</span>
            <h2 className="section-title">Career Opportunities</h2>
            <p className="section-subtitle">
              We are always looking for talented and passionate individuals to join our team. At Providers Care Billing LLC 
              we offer a variety of roles that allow you to grow, innovate, and make a real impact in the healthcare industry. 
              Check out our current openings below and find the perfect opportunity to advance your career with us.
            </p>
          </div>

          <div className="jobs-grid">
            {jobOpenings.map(job => (
              <div className="job-card" key={job.id}>
                <div className="job-header">
                  <h3 className="job-title">{job.title}</h3>
                  <div className="job-meta">
                    <span className="job-type">{job.type}</span>
                    <span className="job-location">{job.location}</span>
                  </div>
                </div>
                <div className="job-skills">
                  <h4>Skills:</h4>
                  <ul>
                    {job.skills.map((skill, idx) => (
                      <li key={idx}>{skill}</li>
                    ))}
                  </ul>
                </div>
                <button className="apply-btn" onClick={() => openJobModal(job)}>
                  See Position → Apply Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="subscribe-section">
        <div className="container">
          <div className="subscribe-content">
            <h2 className="subscribe-title">SUBSCRIBE NOW</h2>
            <p className="subscribe-description">
              Want to Get More Info? Stay updated with the latest news and job openings at Providers Care Billing. 
              Subscribe to our newsletter for insights, career tips, and more!
            </p>
            <form className="subscribe-form" onSubmit={handleSubscribe}>
              <div className="subscribe-input-group">
                <input 
                  type="email" 
                  name="subscribeEmail" 
                  placeholder="Enter your email address" 
                  value={formData.subscribeEmail} 
                  onChange={handleSubscribeChange}
                  required 
                />
                <button type="submit" className="subscribe-btn">Subscribe →</button>
              </div>
              {subscribeStatus.message && (
                <div className={`subscribe-status ${subscribeStatus.type}`}>
                  {subscribeStatus.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="career-badges">
        <div className="container">
          <div className="badges-wrapper-career">
            {/* <div className="badge-item-career">
              <div className="badge-rating">4.8</div>
              <div className="badge-stars">⭐⭐⭐⭐⭐</div>
              <div className="badge-label">Google Rating</div>
            </div> */}
            <div className="badge-item-career">
              <div className="badge-icon">✓</div>
              <div className="badge-label">ACCREDITED BUSINESS</div>
            </div>
            <div className="badge-item-career">
              <div className="badge-icon">🔒</div>
              <div className="badge-label">HIPAA COMPLIANT</div>
            </div>
            <div className="badge-item-career">
              <div className="badge-icon">⚖️</div>
              <div className="badge-label">DMCA PROTECTED</div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Application Modal */}
      {selectedJob && (
        <div className="modal-overlay" onClick={closeJobModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeJobModal}>×</button>
            <h2 className="modal-title">Apply for {selectedJob.title}</h2>
            <p className="modal-location">📍 {selectedJob.location} | {selectedJob.type}</p>
            
            {submitStatus.message && (
              <div className={`modal-status ${submitStatus.type}`}>
                {submitStatus.message}
              </div>
            )}
            
            <form onSubmit={handleJobApplication} className="application-form">
              <div className="form-row">
                <div className="form-group">
                  <input type="text" name="fullName" placeholder="Full Name" value={formData.fullName} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <input type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleChange} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <input type="text" name="experience" placeholder="Years of Experience" value={formData.experience} onChange={handleChange} required />
                </div>
              </div>
              <div className="form-group">
                <textarea name="coverLetter" rows="4" placeholder="Cover Letter / Why do you want to join us?" value={formData.coverLetter} onChange={handleChange} required></textarea>
              </div>
              <div className="form-group">
                <label className="file-label">
                  Upload Resume (PDF/DOC)
                  <input type="file" name="resume" accept=".pdf,.doc,.docx" onChange={handleFileChange} style={{ display: 'none' }} />
                  <span className="file-placeholder">{formData.resume ? formData.resume.name : 'Click to upload resume'}</span>
                </label>
              </div>
              <button type="submit" className="submit-application-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Application →'}
              </button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default Career;